import React, { ReactNode } from "react";
// Components
import SelectBanks from "../select/SelectBanks";
import SelectLifts from "../select/SelectLifts";
import FormFlatbufferMessagePicker from "../form/FormFlatbufferMessagePicker";
// Interfaces
import { FbMessageRef } from "../../../interfaces/FbMessage.interface";
import type { Ln2MappingData } from "./ln2MappingData";

// ---------------------------------------------------------------------------
// Data interface — the LN2 side of any adapter mapping entry.
// ---------------------------------------------------------------------------

// Defined in ./ln2MappingData and re-exported here so the many existing
// `from ".../Ln2MappingEditor"` imports keep working. They live in a separate,
// React-free module because `lobbyVision2CdoBuilder.ts` imports
// `defaultLn2MappingData` at runtime — which used to make the headless
// FlatBuffer builder depend on this component, and on JSX parsing, for anything
// outside the app bundle. See ln2MappingData.ts for the full account.
export type { Ln2MappingData } from "./ln2MappingData";
export { defaultLn2MappingData } from "./ln2MappingData";

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export interface Ln2MappingEditorProps {
  /** Current LN2 mapping data. */
  value: Ln2MappingData;

  /** Called when any LN2 field changes. */
  onChange: (updated: Ln2MappingData) => void;

  /** Disable all inputs (read-only mode). */
  disabled?: boolean;

  /**
   * Optional slot for adapter-specific LN2 fields rendered *below* the
   * message picker. Examples: Modbus position + active toggle, LN1
   * byte/bit offsets, etc.
   *
   * Receives the current `Ln2MappingData` so the slot can conditionally
   * render based on the selected message.
   */
  renderExtraLn2Fields?: ((data: Ln2MappingData) => ReactNode) | null;

  /**
   * When true, hide the bank/lift selectors. Useful for adapters that
   * resolve bank/lift identity through other means (e.g., OPC hierarchical
   * location, BACnet UUID mapping).
   */
  hideBankLift?: boolean;

  /**
   * When true, hide the message picker. Useful for composition with
   * alternative pickers (e.g., LN1's row-based SelectLn2Message).
   */
  hideMessagePicker?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Unified LN2 mapping editor used by all adapter CDO pages.
 *
 * Renders the common LN2 fields that every adapter mapping needs:
 * - **Bank selector** with toggle (SelectBanks)
 * - **Lift selector** cascading from the selected bank (SelectLifts)
 * - **LN2 message picker** (FormFlatbufferMessagePicker)
 *
 * Adapter-specific LN2 fields (position, active, byte offsets, etc.) are
 * injected via the `renderExtraLn2Fields` render prop.
 *
 * Vendor-specific fields (register addresses, OPC paths, pins) live in
 * the adapter's own component — this component only handles the LN2 output
 * side of the mapping.
 */
export default function Ln2MappingEditor({
  value,
  onChange,
  disabled = false,
  renderExtraLn2Fields = undefined,
  hideBankLift = false,
  hideMessagePicker = false,
}: Ln2MappingEditorProps) {
  // **************************************************************** */
  // HANDLERS ******************************************************* */

  const onChangeBank = (id: string, name: string) => {
    onChange({
      ...value,
      bank_id: id,
      bank_name: name,
      // Reset lift when bank changes.
      lift_id: "",
      lift_name: "",
    });
  };

  const onChangeLift = (id: string, name: string) => {
    onChange({
      ...value,
      lift_id: id,
      lift_name: name,
    });
  };

  const onChangeMessage = (ref: FbMessageRef | null) => {
    onChange({
      ...value,
      message: ref,
    });
  };

  // **************************************************************** */
  // RENDER ********************************************************* */

  return (
    <>
      {/* ---- Bank selector ---- */}
      {!hideBankLift && (
        <>
          <div className="col-md-12 mt-3">
            <div className="generalStyles__flex">
              <div className="generalStyles__inputFlexNameLarge">
                <span className="generalStyles__inputFlexRequired">*</span>
                <span className="drawer__text">LN2 Bank</span>
              </div>
              <div className="generalStyles__width100">
                <SelectBanks
                  inputValue={value.bank_id}
                  inputOnChange={(id: string, name: string) => onChangeBank(id, name)}
                  disabledInput={disabled}
                />
              </div>
              <div className="generalStyles__infoTooltipIconBlank" />
            </div>
          </div>

          {/* ---- Lift selector ---- */}
          <div className="col-md-12 mt-3">
            <div className="generalStyles__flex">
              <div className="generalStyles__inputFlexNameLarge">
                <span className="generalStyles__inputFlexRequired">*</span>
                <span className="drawer__text">LN2 Lift</span>
              </div>
              <div className="generalStyles__width100">
                <SelectLifts
                  inputValue={value.lift_id}
                  inputOnChange={(id: string, name: string) => onChangeLift(id, name)}
                  disabledInput={disabled}
                  bankId={value.bank_id}
                />
              </div>
              <div className="generalStyles__infoTooltipIconBlank" />
            </div>
          </div>
        </>
      )}

      {/* ---- LN2 message picker ---- */}
      {!hideMessagePicker && (
        <FormFlatbufferMessagePicker
          value={value.message}
          onChange={onChangeMessage}
          disabled={disabled}
        />
      )}

      {/* ---- Adapter-specific extra LN2 fields (slot) ---- */}
      {renderExtraLn2Fields?.(value)}
    </>
  );
}
